import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { fetchAndyNotes } from '../../api/notesApi';
import { formatTimeStamp } from '../../api/callData';

// Helper function to highlight search text
const highlightText = (text, searchTerm) => {
  if (!searchTerm || !text) return text;
  
  const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  
  return parts.map((part, index) => 
    regex.test(part) ? (
      <span key={index} className="bg-yellow-200 text-yellow-900 px-1 rounded">
        {part}
      </span>
    ) : part
  );
};



const AdminNotesTab = ({ searchQuery }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load notes from API
  useEffect(() => {
    const loadNotes = async () => {
      try {
        setLoading(true);
        const response = await fetchAndyNotes();
        const notesList = response.notes || [];
        setNotes(notesList);
        setError(null);
        
        // Update the count in the parent component
        if (window.updateAdminCount) {
          const filteredNotesList = searchQuery.trim() === '' 
            ? notesList 
            : notesList.filter(note => {
                const searchLower = searchQuery.toLowerCase();
                return (
                  (note.caller_id && note.caller_id.toLowerCase().includes(searchLower)) ||
                  (note.note_text && note.note_text.toLowerCase().includes(searchLower))
                );
              });
          
          window.updateAdminCount('notesCount', filteredNotesList.length);
        }
      } catch (err) {
        setError('Failed to load notes');
        console.error('Error loading notes:', err);
        setNotes([]);
        
        // Update count to 0 on error
        if (window.updateAdminCount) {
          window.updateAdminCount('notesCount', 0);
        }
      } finally {
        setLoading(false);
      }
    };

    loadNotes();
  }, [searchQuery]);

  // Filter notes based on search query
  const filteredNotes = searchQuery.trim() === '' 
    ? notes 
    : notes.filter(note => {
        const searchLower = searchQuery.toLowerCase();
        const searchableFields = [
          note.caller_id,
          note.note_text,
          note.timestamp
        ].filter(Boolean); // Remove undefined/null values
        
        return searchableFields.some(field => 
          field.toString().toLowerCase().includes(searchLower)
        );
      });
  
  // Update the count in the parent component whenever filtered results change
  useEffect(() => {
    if (window.updateAdminCount) {
      window.updateAdminCount('notesCount', filteredNotes.length);
    }
  }, [filteredNotes.length, searchQuery]);

  // Always update count when component mounts or search changes, regardless of visibility
  useEffect(() => {
    if (window.updateAdminCount) {
      window.updateAdminCount('notesCount', filteredNotes.length);
    }
  }, [filteredNotes.length, searchQuery]);

  return (
    <div className="flex flex-1 gap-2 w-full min-h-0 overflow-hidden">
      <div className="flex flex-col w-full transition-all duration-300 ease-out overflow-hidden">
        <div className="w-full h-full flex flex-col">
          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto scrollable">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-font-secondary">Loading notes...</div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-error">{error}</div>
              </div>
            ) : filteredNotes.length === 0 ? (
              <div className="text-center py-10 text-font-secondary">
                {searchQuery.trim() !== '' ? 
                  `No notes found matching "${searchQuery}"` : 
                  "No notes available"}
              </div>
            ) : (
              <>
                {/* Desktop List View (xl and above) */}
                <div className="hidden xl:block">
                  {filteredNotes.map((note) => (
                    <div
                      key={note.id}
                      className="flex py-2 text-font-secondary hover:text-font-primary hover:bg-background hover:button-container cursor-pointer transition-colors ease-out-in duration-300 text-sm"
                    >
                      <div className="flex flex-col gap-1 w-[140px] max-w-[140px] text-font-primary flex-shrink-0">
                        <span className="font-semibold text-primary text-sm">{note.caller_id}</span>
                        <span className="text-sm text-font-secondary">{formatTimeStamp(note.timestamp)}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm leading-relaxed">
                          {note.note_text}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Mobile Card View (below xl) */}
                <div className="xl:hidden p-2">
                  {filteredNotes.map((note) => (
                    <div
                      key={note.id}
                      className="p-3 rounded-lg cursor-pointer transition-all duration-300 ease-out-in hover:shadow-lg hover:scale-[1.02] bg-bgSecondary card-container border border-bgAccent min-h-[60px] flex flex-col mb-2"
                    >
                      <div className="flex items-start justify-between mb-2 flex-shrink-0">
                        <div className="flex items-center gap-2">
                                                  <div className="flex flex-col">
                          <span className="font-semibold text-primary text-base">
                            {note.caller_id}
                          </span>
                          <span className="text-xs text-font-secondary">
                            {formatTimeStamp(note.timestamp)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="mt-2">
                      <p className="text-sm text-font-secondary leading-relaxed">
                        {note.note_text}
                      </p>
                    </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminNotesTab;